/**
 * 甘特图任务条组件
 *
 * 显示单个任务的水平条形，支持拖拽调整日期
 * 支持工作流阶段进度显示、超时状态高亮、回退标记
 */

import { RotateCcw } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { DEFAULT_ROW_HEIGHT, PRIORITY_COLORS, WORKFLOW_STAGE_LABELS, WORKFLOW_STAGE_COLORS } from './types'
import { calculateTaskPosition } from './utils'

import type { GanttTask, ZoomLevel, DateRange, DragEdge } from './types'

interface TaskBarProps {
  /** 任务数据 */
  task: GanttTask
  /** 缩放级别 */
  zoomLevel: ZoomLevel
  /** 日期范围 */
  dateRange: DateRange
  /** 行高 */
  rowHeight?: number
  /** 是否正在拖拽 */
  isDragging: boolean
  /** 拖拽位移 */
  deltaX?: number
  /** 拖拽边缘 */
  dragEdge?: DragEdge | null
  /** 拖拽开始回调 */
  onDragStart: (edge: DragEdge) => void
  /** 拖拽中回调 */
  onDrag: (deltaX: number) => void
  /** 拖拽结束回调 */
  onDragEnd: () => void
  /** 点击回调 */
  onClick: () => void
}

export function TaskBar({
  task,
  zoomLevel,
  dateRange,
  rowHeight = DEFAULT_ROW_HEIGHT,
  isDragging,
  deltaX = 0,
  dragEdge = null,
  onDragStart,
  onDrag,
  onDragEnd,
  onClick,
}: TaskBarProps) {
  const barRef = React.useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = React.useState(false)

  // 计算任务位置
  const position = React.useMemo(
    () => calculateTaskPosition(task, dateRange, zoomLevel),
    [task, dateRange, zoomLevel]
  )

  // 处理拖拽位移的实时位置
  const dragStyle = React.useMemo(() => {
    if (!isDragging || deltaX === 0) {return {}}

    const style: React.CSSProperties = {}
    if (dragEdge === 'body') {
      style.transform = `translateX(${deltaX}px)`
    } else if (dragEdge === 'left') {
      style.left = position.left + deltaX
      style.width = Math.max(position.width - deltaX, 10) // 最小宽度
    } else if (dragEdge === 'right') {
      style.width = Math.max(position.width + deltaX, 10) // 最小宽度
    }
    return style
  }, [isDragging, deltaX, dragEdge, position])

  // 获取优先级颜色
  const priorityColor = PRIORITY_COLORS[task.priority]

  // 处理拖拽开始 (统一处理鼠标和触摸)
  const handleDragStartInternal = React.useCallback(
    (clientX: number, edge: DragEdge) => {
      onDragStart(edge)
      const startX = clientX

      // 处理移动
      const handleMove = (currentClientX: number) => {
        const currentDeltaX = currentClientX - startX
        onDrag(currentDeltaX)
      }

      // 结束拖拽
      const handleEnd = () => {
        // 清理所有事件监听
        document.removeEventListener('mousemove', mouseMoveHandler)
        document.removeEventListener('mouseup', mouseUpHandler)
        document.removeEventListener('touchmove', touchMoveHandler)
        document.removeEventListener('touchend', touchEndHandler)
        onDragEnd()
      }

      // 鼠标事件处理
      const mouseMoveHandler = (e: MouseEvent) => handleMove(e.clientX)
      const mouseUpHandler = () => handleEnd()

      // 触摸事件处理
      const touchMoveHandler = (e: TouchEvent) => {
        // 防止触摸拖拽时页面滚动
        if (e.cancelable) {e.preventDefault()}
        handleMove(e.touches[0].clientX)
      }
      const touchEndHandler = () => handleEnd()

      // 绑定全局监听
      document.addEventListener('mousemove', mouseMoveHandler)
      document.addEventListener('mouseup', mouseUpHandler)
      document.addEventListener('touchmove', touchMoveHandler, { passive: false })
      document.addEventListener('touchend', touchEndHandler)
    },
    [onDragStart, onDrag, onDragEnd]
  )

  // 处理鼠标按下
  const handleMouseDown = (e: React.MouseEvent, edge: DragEdge) => {
    e.preventDefault()
    e.stopPropagation()
    handleDragStartInternal(e.clientX, edge)
  }

  // 处理触摸开始
  const handleTouchStart = (e: React.TouchEvent, edge: DragEdge) => {
    // 阻止默认行为（如滚动）在某些浏览器可能需要，但在 React 的合成事件中，
    // passive: true 是默认的，所以 preventDefault 可能无效，或者需要 ref 绑定。
    // 这里主要在 move 中阻止。
    e.stopPropagation()
    const touch = e.touches[0]
    handleDragStartInternal(touch.clientX, edge)
  }

  // 处理点击
  const handleClick = React.useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation()
      onClick()
    },
    [onClick]
  )

  // 计算任务条高度（留出上下边距）
  const barHeight = rowHeight - 8
  const barTop = 4

  return (
    <div
      ref={barRef}
      className={cn(
        'absolute flex cursor-pointer items-center rounded transition-shadow',
        isDragging && 'z-30 shadow-lg',
        isHovered && !isDragging && 'shadow-md touch-none', // touch-none 防止浏览器默认手势干扰
        task.isOverdue && 'ring-2 ring-red-500',
        task.isRolledBack && 'ring-2 ring-amber-500'
      )}
      style={{
        left: position.left,
        width: position.width,
        top: barTop,
        height: barHeight,
        ...dragStyle,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      onTouchEnd={(e) => {
        // 简单的点击兼容，如果是拖拽结束不会触发 onClick，这里主要是为了非拖拽的点击
        // 实际上 React onClick 对 touch 也有兼容，但为了保险
        e.stopPropagation()
        // 不要在这里调用 onClick，因为可能会和 mouse click 重复
      }}
    >
      {/* 左边缘拖拽手柄 */}
      <div
        className="absolute left-0 top-0 z-10 h-full w-4 -translate-x-1/2 cursor-ew-resize touch-none rounded-l bg-transparent hover:bg-black/10 text-transparent"
        onMouseDown={(e) => handleMouseDown(e, 'left')}
        onTouchStart={(e) => handleTouchStart(e, 'left')}
      />

      {/* 任务条主体 */}
      <div
        className={cn(
          'relative flex h-full w-full items-center overflow-hidden rounded touch-none',
          // 状态颜色: 进行中=蓝色, 已完成=绿色, 待办=灰色, 阻塞=红色
          task.status === 'done'
            ? 'bg-green-500/80'
            : task.status === 'cancelled'
              ? 'bg-gray-400/60'
              : task.status === 'blocked'
                ? 'bg-red-400/80'
                : task.status === 'backlog' || task.status === 'todo'
                  ? 'bg-gray-500/70'
                  : 'bg-primary/80' // in_progress, code_review, testing
        )}
        onMouseDown={(e) => handleMouseDown(e, 'body')}
        onTouchStart={(e) => handleTouchStart(e, 'body')}
      >
        {/* 进度条 */}
        <div
          className="absolute inset-y-0 left-0 bg-black/20"
          style={{ width: `${task.progress}%` }}
        />

        {/* 优先级指示器 */}
        <div
          className={cn('absolute left-0 top-0 h-full w-1', priorityColor)}
        />

        {/* 工作流阶段指示器 */}
        {task.currentStage && (
          <div
            className={cn(
              'absolute right-0 top-0 h-full w-1',
              WORKFLOW_STAGE_COLORS[task.currentStage]
            )}
          />
        )}

        {/* 回退标记 */}
        {task.isRolledBack && (
          <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10">
            <RotateCcw className="h-3 w-3 text-amber-200" />
          </div>
        )}

        {/* 任务标题 */}
        <span className="relative z-10 truncate px-2 text-xs font-medium text-white">
          {task.code} {task.title}
        </span>
      </div>

      {/* 右边缘拖拽手柄 */}
      <div
        className="absolute right-0 top-0 z-10 h-full w-2 cursor-ew-resize rounded-r bg-transparent hover:bg-black/10"
        onMouseDown={(e) => handleMouseDown(e, 'right')}
      />

      {/* Tooltip */}
      {isHovered && !isDragging && (
        <TaskBarTooltip task={task} />
      )}
    </div>
  )
}

// ==================== 子组件 ====================

interface TaskBarTooltipProps {
  task: GanttTask
}

function TaskBarTooltip({ task }: TaskBarTooltipProps) {
  // 计算持续时间
  const duration = React.useMemo(() => {
    const start = new Date(task.startDate)
    const end = new Date(task.endDate)
    const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    return Math.max(days, 1)
  }, [task.startDate, task.endDate])

  return (
    <div className="absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-3 py-2 text-sm shadow-lg border">
      <div className="font-medium">{task.code} {task.title}</div>
      <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <span>开始:</span>
          <span>{task.startDate}</span>
          {task.isStartDateCalculated && (
            <span className="text-amber-500 text-[10px]">(自动计算)</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span>截止:</span>
          <span>{task.endDate}</span>
          {task.isEndDateCalculated && (
            <span className="text-amber-500 text-[10px]">(自动计算)</span>
          )}
        </div>
        <div>持续: {duration} 天</div>
        <div>进度: {task.progress}%</div>
        {task.assigneeName && <div>负责人: {task.assigneeName}</div>}
        {task.currentStage && (
          <div className="flex items-center gap-1">
            <span>阶段:</span>
            <span className={cn(
              'inline-block rounded px-1 text-white text-xs',
              WORKFLOW_STAGE_COLORS[task.currentStage]
            )}>
              {WORKFLOW_STAGE_LABELS[task.currentStage]}
            </span>
          </div>
        )}
        {task.isOverdue && (
          <div className="mt-1 font-medium text-red-500">⚠ 已超时</div>
        )}
        {task.isRolledBack && (
          <div className="mt-1 font-medium text-amber-500">↩ 已回退</div>
        )}
      </div>
      {/* Tooltip 箭头 */}
      <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-popover border-l border-t" />
    </div>
  )
}
